import { useEffect, useMemo, useState } from "react";
import { Plus, X, Loader2, Clock, Trash2, SplitSquareHorizontal, Users, CheckCircle2, DollarSign } from "lucide-react";
import api from "@/lib/api";

const Mono = ({ children, className = "", ...rest }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`} {...rest}>{children}</span>
);
const inp = "w-full bg-navy-950 border border-gold-500/20 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-gold-500";
const lbl = "font-mono uppercase tracking-[0.2em] text-[0.55rem] text-white/45 block mb-1.5";
const today = () => new Date().toISOString().slice(0, 10);

export default function TimeCapture() {
  const [meta, setMeta] = useState({ activities: [], is_supervisor: false });
  const [tab, setTab] = useState("mine");
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [claims, setClaims] = useState([]);
  const [allocating, setAllocating] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushMsg, setPushMsg] = useState("");

  const pushToCosts = async () => {
    setPushing(true); setPushMsg("");
    try {
      const { data } = await api.post("/timecapture/push-to-costitems");
      setPushMsg(data.created ? `Pushed ${data.created} allocation(s) — $${data.total_cost.toLocaleString()} into cost items.` : "Nothing new to push — all allocations already in cost items.");
    } catch { setPushMsg("Push failed — try again."); }
    finally { setPushing(false); }
  };

  const load = async (t = tab) => {
    const [e, s] = await Promise.all([
      api.get(`/timecapture/entries?scope=${t === "team" ? "team" : "mine"}`),
      api.get("/timecapture/summary"),
    ]);
    setEntries(e.data); setSummary(s.data);
  };
  useEffect(() => {
    api.get("/timecapture/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/fieldclaim/claims").then((r) => setClaims(r.data)).catch(() => {});
    load();
  }, []); // eslint-disable-line
  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line

  const claimName = (id) => claims.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-8 text-white" data-testid="timecapture-page">
      <div>
        <Mono className="text-gold-400 text-[0.65rem]">TimeCapture · actual cost engine</Mono>
        <h1 className="font-heading font-light text-4xl tracking-tight mt-3">TimeCapture</h1>
        <p className="text-white/55 mt-3 font-light max-w-2xl">
          Crews self-record hours against an event; supervisors allocate those hours (with rates) across
          damaged assets — building the day-labour actuals ledger behind every claim.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-3" data-testid="tc-summary-stats">
          {[["Total hours", `${summary.total_hours}h`, "text-gold-300"],
            ["Allocated actuals", `$${summary.total_allocated_cost.toLocaleString()}`, "text-emerald-300"],
            ["Unallocated hours", `${summary.unallocated_hours}h`, summary.unallocated_hours > 0 ? "text-amber-300" : "text-white/60"]].map(([k, v, c]) => (
            <div key={k} className="border border-gold-500/25 bg-navy-900/50 px-5 py-4">
              <Mono className="text-[0.5rem] text-white/40">{k}</Mono>
              <div className={`font-mono text-2xl mt-1 ${c}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {meta.is_supervisor && summary && summary.total_allocated_cost > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={pushToCosts} disabled={pushing} data-testid="tc-push-costs-btn"
            className="inline-flex items-center gap-2 border border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 px-4 py-2.5 text-sm transition-colors disabled:opacity-50">
            {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />} Push actuals → claim cost items
          </button>
          {pushMsg && <span className="text-gold-300 text-sm" data-testid="tc-push-result">{pushMsg}</span>}
        </div>
      )}

      <div className="flex items-center gap-1.5" data-testid="tc-tabs">
        {[["mine", "My timesheet", Clock], ...(meta.is_supervisor ? [["team", "Team & allocation", Users]] : [])].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tc-tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border transition-colors ${tab === k ? "bg-gold-500 text-navy-950 font-semibold border-gold-500" : "border-gold-500/25 text-white/60 hover:text-gold-300"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "mine" && <LogForm activities={meta.activities} claims={claims} onSaved={() => load()} />}

      <EntriesTable entries={entries} team={tab === "team"} claimName={claimName}
        canAllocate={meta.is_supervisor && tab === "team"}
        onAllocate={(e) => setAllocating(e)}
        onDelete={async (id) => { if (window.confirm("Delete this time entry?")) { await api.delete(`/timecapture/entries/${id}`); load(); } }} />

      {summary && summary.by_asset.length > 0 && (
        <div className="border border-gold-500/25 bg-navy-900/40 p-6" data-testid="tc-by-asset">
          <Mono className="text-[0.55rem] text-white/50">Actuals by asset</Mono>
          <table className="w-full mt-4 text-sm">
            <thead><tr className="text-left border-b border-gold-500/15">
              {["Asset", "Event", "Hours", "Actual cost"].map((h) => <th key={h} className="py-2 font-mono uppercase tracking-widest text-[0.55rem] text-white/40">{h}</th>)}
            </tr></thead>
            <tbody>
              {summary.by_asset.map((r) => (
                <tr key={r.asset_id} className="border-b border-white/5">
                  <td className="py-2.5 text-white/80">{r.asset_name}</td>
                  <td className="py-2.5 text-white/45 font-light">{claimName(r.claim_id)}</td>
                  <td className="py-2.5 font-mono text-gold-300">{r.hours}h</td>
                  <td className="py-2.5 font-mono text-emerald-300">${r.cost.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {allocating && <AllocateModal entry={allocating} onClose={() => setAllocating(null)} onSaved={() => { setAllocating(null); load(); }} />}
    </div>
  );
}

function LogForm({ activities, claims, onSaved }) {
  const [form, setForm] = useState({ date: today(), hours: "", activity: "Labour", claim_id: "", note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/timecapture/entries", { ...form, hours: Number(form.hours), claim_id: form.claim_id || null });
      setForm({ date: today(), hours: "", activity: form.activity, claim_id: form.claim_id, note: "" });
      onSaved();
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-gold-500/25 bg-navy-900/40 p-5 grid md:grid-cols-6 gap-3 items-end" data-testid="tc-log-form">
      <div><label className={lbl}>Date</label><input type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inp} data-testid="tc-date" /></div>
      <div><label className={lbl}>Hours</label><input type="number" step="0.25" min="0.25" max="24" required placeholder="7.5" value={form.hours} onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))} className={inp} data-testid="tc-hours" /></div>
      <div><label className={lbl}>Activity</label>
        <select value={form.activity} onChange={(e) => setForm((f) => ({ ...f, activity: e.target.value }))} className={inp} data-testid="tc-activity">
          {activities.map((a) => <option key={a}>{a}</option>)}
        </select></div>
      <div><label className={lbl}>Event</label>
        <select value={form.claim_id} onChange={(e) => setForm((f) => ({ ...f, claim_id: e.target.value }))} className={inp} data-testid="tc-claim">
          <option value="">— none —</option>
          {claims.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select></div>
      <div><label className={lbl}>Note</label><input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Culvert clearing" className={inp} data-testid="tc-note" /></div>
      <button type="submit" disabled={busy || !form.hours} data-testid="tc-log-submit"
        className="bg-gold-500 text-navy-950 font-semibold py-2.5 px-4 hover:bg-gold-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Log
      </button>
    </form>
  );
}

function EntriesTable({ entries, team, claimName, canAllocate, onAllocate, onDelete }) {
  if (entries.length === 0) {
    return <div className="border border-dashed border-gold-500/25 p-10 text-center text-white/40 font-light text-sm" data-testid="tc-empty">No time entries yet.</div>;
  }
  return (
    <div className="border border-gold-500/25 bg-navy-900/40 p-6 overflow-x-auto" data-testid="tc-entries">
      <table className="w-full text-sm min-w-[640px]">
        <thead><tr className="text-left border-b border-gold-500/15">
          {[...(team ? ["Person"] : []), "Date", "Hours", "Activity", "Event", "Status", ""].map((h, i) => <th key={i} className="py-2 font-mono uppercase tracking-widest text-[0.55rem] text-white/40">{h}</th>)}
        </tr></thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} className="border-b border-white/5" data-testid={`tc-entry-${e.id}`}>
              {team && <td className="py-2.5 text-white/80">{e.user_name}</td>}
              <td className="py-2.5 font-mono text-xs text-white/60">{e.date}</td>
              <td className="py-2.5 font-mono text-gold-300">{e.hours}h</td>
              <td className="py-2.5 text-white/70 font-light">{e.activity}{e.note ? <span className="text-white/35"> · {e.note}</span> : null}</td>
              <td className="py-2.5 text-white/45 font-light">{claimName(e.claim_id)}</td>
              <td className="py-2.5">
                {e.status === "allocated" ? (
                  <span className="inline-flex items-center gap-1 border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 text-[0.6rem] font-mono uppercase px-2 py-0.5 rounded-sm"><CheckCircle2 className="h-3 w-3" /> allocated</span>
                ) : (
                  <span className="border border-white/15 text-white/45 text-[0.6rem] font-mono uppercase px-2 py-0.5 rounded-sm">logged</span>
                )}
              </td>
              <td className="py-2.5 text-right whitespace-nowrap">
                {canAllocate && (
                  <button onClick={() => onAllocate(e)} data-testid={`tc-allocate-${e.id}`} title="Allocate hours to assets"
                    className="text-gold-300/80 hover:text-gold-300 mr-3"><SplitSquareHorizontal className="h-4 w-4 inline" /></button>
                )}
                {e.status !== "allocated" && (
                  <button onClick={() => onDelete(e.id)} data-testid={`tc-delete-${e.id}`} className="text-red-300/60 hover:text-red-300"><Trash2 className="h-4 w-4 inline" /></button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AllocateModal({ entry, onClose, onSaved }) {
  const [claims, setClaims] = useState([]);
  const [claimId, setClaimId] = useState(entry.claim_id || "");
  const [assets, setAssets] = useState([]);
  const [rows, setRows] = useState(entry.allocations?.length ? entry.allocations.map((a) => ({ asset_id: a.asset_id, hours: a.hours, rate: a.rate })) : [{ asset_id: "", hours: entry.hours, rate: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/fieldclaim/claims").then((r) => setClaims(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (claimId) api.get(`/fieldclaim/claims/${claimId}/assets`).then((r) => setAssets(r.data)).catch(() => setAssets([]));
    else setAssets([]);
  }, [claimId]);

  const totalH = useMemo(() => rows.reduce((s, r) => s + (Number(r.hours) || 0), 0), [rows]);
  const totalC = useMemo(() => rows.reduce((s, r) => s + (Number(r.hours) || 0) * (Number(r.rate) || 0), 0), [rows]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const allocations = rows.filter((r) => r.asset_id && Number(r.hours) > 0)
        .map((r) => ({ asset_id: r.asset_id, hours: Number(r.hours), rate: Number(r.rate) || 0 }));
      await api.post(`/timecapture/entries/${entry.id}/allocate`, { allocations });
      onSaved();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Could not allocate."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="tc-allocate-modal" className="bg-navy-900 border border-gold-500/30 w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto text-white">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl">Allocate {entry.hours}h — {entry.user_name}</h2>
          <button onClick={onClose} data-testid="tc-allocate-close"><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <div>
          <label className={lbl}>Event</label>
          <select value={claimId} onChange={(e) => setClaimId(e.target.value)} className={inp} data-testid="tc-alloc-claim">
            <option value="">— select event —</option>
            {claims.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="space-y-2.5">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_100px_32px] gap-2 items-center">
              <select value={r.asset_id} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, asset_id: e.target.value } : x))} className={inp} data-testid={`tc-alloc-asset-${i}`}>
                <option value="">— asset —</option>
                {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <input type="number" step="0.25" min="0" placeholder="hrs" value={r.hours} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, hours: e.target.value } : x))} className={inp} data-testid={`tc-alloc-hours-${i}`} />
              <input type="number" step="0.5" min="0" placeholder="$/hr" value={r.rate} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))} className={inp} data-testid={`tc-alloc-rate-${i}`} />
              <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} className="text-red-300/60 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => setRows((rs) => [...rs, { asset_id: "", hours: "", rate: rows[rows.length - 1]?.rate || "" }])} data-testid="tc-alloc-add-row"
            className="text-gold-300 text-xs inline-flex items-center gap-1.5 hover:text-gold-200"><Plus className="h-3.5 w-3.5" /> Split across another asset</button>
        </div>
        <div className="flex items-center justify-between border-t border-gold-500/15 pt-3 text-sm">
          <span className={`font-mono ${totalH > entry.hours ? "text-red-300" : "text-white/60"}`}>{totalH}h / {entry.hours}h</span>
          <span className="font-mono text-emerald-300">${totalC.toLocaleString()}</span>
        </div>
        {err && <div className="text-red-300 text-sm" data-testid="tc-alloc-error">{String(err)}</div>}
        <button onClick={submit} disabled={busy || totalH > entry.hours} data-testid="tc-alloc-save"
          className="w-full bg-gold-500 text-navy-950 font-semibold py-3 hover:bg-gold-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SplitSquareHorizontal className="h-4 w-4" />} Save allocation
        </button>
      </div>
    </div>
  );
}
